/**
 * Admin service for managing event applications
 * 
 * This service handles admin operations for event applications
 */
import { logger } from '../utils/logger';
import { getApiBaseUrl } from '../utils/environmentConfig';
import { getAuth } from 'firebase/auth';

// Firebase Cloud Function URLs using environment configuration
const ADMIN_SCAN_QR_ENDPOINT = `${getApiBaseUrl()}/verifyQRCode`;

/**
 * Scan a QR code for event check-in
 * @param applicationId The application ID
 * @returns Promise with the scan result
 */
export const scanQrCode = async (applicationId: string): Promise<{
  success: boolean;
  message: string;
  name?: string;
  email?: string;
  applicationId?: string;
  attendeeType?: string;
}> => {
  logger.info('🎟️ Scanning QR code for check-in');
  
  try {
    // Get current user token for authentication
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to scan QR codes');
    }

    // Force token refresh to ensure we have the latest claims
    await currentUser.getIdToken(true);
    
    // Import here to avoid circular dependencies
    const { checkAdminClaims } = await import('../utils/adminClaims');
    
    // Verify admin access
    const isAdmin = await checkAdminClaims(currentUser);
    if (!isAdmin) {
      logger.error('❌ Admin access required for scanning QR codes');
      throw new Error('Admin access required');
    }

    const token = await currentUser.getIdToken();
    
    // Log the request details for debugging
    logger.info('📤 Sending QR verification request', { 
      endpoint: ADMIN_SCAN_QR_ENDPOINT,
      applicationId,
      hasToken: !!token,
      tokenLength: token?.length
    });
    
    const response = await fetch(ADMIN_SCAN_QR_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ applicationId }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      logger.error('❌ Failed to process QR code:', data);
      throw new Error(data.error || 'Failed to process QR code');
    }
    
    logger.success('✅ QR code processed successfully');
    return data;
  } catch (error) {
    logger.error('❌ Error in scanQrCode:', error);
    throw error;
  }
};